<template>
  <div class="color-picker-container">
    <div class="color-picker-wrapper">
      <el-color-picker 
        v-model="colorValue" 
        @change="handleColorChange"
        @active-change="handleColorChange"
        :predefine="predefineColors"
      />
      <el-button 
        class="eyedropper-btn" 
        :class="{ 'color-pick-success': animateSuccess }"
        size="mini" 
        @click="startEyeDropper"
        :disabled="!isEyeDropperSupported || isPickingColor"
        :title="getButtonTitle()"
      >
        <i v-if="!isPickingColor" class="eyedropper-icon">🎨</i>
        <i v-else class="el-icon-loading"></i>
      </el-button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ColorPicker',
  props: {
    value: {
      type: String,
      default: '#ffffff'
    }
  },
  data() {
    return {
      colorValue: this.value,
      isEyeDropperSupported: false,
      isPickingColor: false,
      animateSuccess: false,
      predefineColors: [
        '#ff4500',
        '#ff8c00',
        '#ffd700',
        '#90ee90',
        '#00ced1',
        '#1e90ff',
        '#c71585',
        '#ff1493',
        '#000000',
        '#ffffff'
      ]
    }
  },
  watch: {
    value(newVal) {
      this.colorValue = newVal
    },
    colorValue(newVal) {
      this.$emit('input', newVal)
      this.$emit('change', newVal)
    }
  },
  mounted() {
    // 检查浏览器是否支持 EyeDropper API
    this.isEyeDropperSupported = 'EyeDropper' in window
  },
  methods: {
    handleColorChange(color) {
      this.$emit('input', color)
      this.$emit('change', color)
    },
    
    getButtonTitle() {
      if (!this.isEyeDropperSupported) {
        return '您的浏览器不支持颜色吸取器功能'
      }
      if (this.isPickingColor) {
        return '正在选取颜色...'
      }
      return '点击使用颜色吸取器'
    },

    async startEyeDropper() {
      if (!this.isEyeDropperSupported) {
        this.$message.warning('您的浏览器不支持颜色吸取器功能')
        return
      }

      this.isPickingColor = true
      
      // 通知父组件开始颜色选取，需要隐藏侧边栏
      this.$emit('eyedropper-start')

      try {
        const eyeDropper = new EyeDropper()
        const result = await eyeDropper.open()
        
        if (result && result.sRGBHex) {
          // 先设置内部值
          this.colorValue = result.sRGBHex
          
          // 使用nextTick确保DOM更新后再触发事件
          this.$nextTick(() => {
            // 确保事件正确触发
            this.$emit('input', result.sRGBHex)
            this.$emit('change', result.sRGBHex)
          })
          
          // 触发成功动画
          this.animateSuccess = true
          setTimeout(() => {
            this.animateSuccess = false
          }, 300)
          
          this.$message.success(`颜色选取成功！选中颜色: ${result.sRGBHex}`)
        }
      } catch (error) {
        // 用户取消了颜色选择或发生其他错误
        if (error.name !== 'AbortError') {
          this.$message.error('颜色吸取失败，请重试')
          console.error('EyeDropper error:', error)
        }
      } finally {
        this.isPickingColor = false
        
        // 通知父组件颜色选取结束，需要显示侧边栏
        this.$emit('eyedropper-end')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.color-picker-container {
  display: inline-block;
}

.color-picker-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
}

.eyedropper-btn {
  min-width: 32px;
  height: 32px;
  padding: 6px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover:not(:disabled) {
    border-color: #409eff;
    background: #ecf5ff;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(64, 158, 255, 0.2);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(64, 158, 255, 0.3);
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    background: #f5f7fa;
  }
}

.eyedropper-icon {
  font-style: normal;
  font-size: 16px;
  line-height: 1;
}

:deep(.el-color-picker) {
  .el-color-picker__trigger {
    border: 1px solid #dcdfe6;
    width: 32px;
    height: 32px;
    border-radius: 4px;
    
    &:hover {
      border-color: #409eff;
    }
  }
}

// 添加一些动画效果
@keyframes colorPickSuccess {
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

.color-pick-success {
  animation: colorPickSuccess 0.3s ease-in-out;
}
</style>
